import type { FoodBenefitOrder, FoodBenefitParseResult, FoodBenefitRecord } from './foodBenefitTypes';
import { FOOD_BENEFIT_DISTRIBUTION_KEYS, createEmptyFoodBenefitDistribution } from './foodBenefitTypes';
import { parseCajuFile } from './parsers/cajuParser';

const createEmptyTotals = () => ({
  dependents: 0,
  feeCount: 0,
  amount: 0,
  breakdown: createEmptyFoodBenefitDistribution(),
});

export async function parseFoodBenefitFiles(files: File[]): Promise<FoodBenefitParseResult> {
  if (!files.length) {
    return { records: [], totals: createEmptyTotals(), orders: [] };
  }

  const parsedRecords: FoodBenefitRecord[] = [];
  const parsedOrders: FoodBenefitOrder[] = [];

  for (const file of files) {
    try {
      // Defaulting to Caju parser as it is the only one currently.
      // In the future, we can add sniffing logic here.
      const { records, order } = await parseCajuFile(file);

      parsedRecords.push(...records);
      if (order) {
        parsedOrders.push({ ...order, fileName: file.name });
      }
    } catch (error) {
      console.error('[parseFoodBenefitFiles] failed to parse file', file.name, error);
      throw error;
    }
  }

  const aggregatedRecordsMap = new Map<string, FoodBenefitRecord>();

  parsedRecords.forEach((record) => {
    const existing = aggregatedRecordsMap.get(record.code);
    if (existing) {
      existing.amount += record.amount;
      existing.dependentsCount += record.dependentsCount;
      existing.feeCount += record.feeCount;
      FOOD_BENEFIT_DISTRIBUTION_KEYS.forEach((key) => {
        existing[key] += record[key];
      });
    } else {
      aggregatedRecordsMap.set(record.code, { ...record });
    }
  });

  const finalRecords = Array.from(aggregatedRecordsMap.values());

  const totals = createEmptyTotals();
  finalRecords.forEach((record) => {
    totals.amount += record.amount;
    totals.dependents += record.dependentsCount;
    totals.feeCount += record.feeCount;
    FOOD_BENEFIT_DISTRIBUTION_KEYS.forEach((key) => {
      totals.breakdown[key] += record[key];
    });
  });

  return { records: finalRecords, totals, orders: parsedOrders };
}
